import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../axiosConfig';
import axios from 'axios';
import styles from './Orchestrator.module.css';
import SummaryDisplayChat from '../MicroProbe/Companies/SummaryDisplay_Chat';

const SPEED_NAMES = ['Quick', 'Fast', 'Balanced', 'Detailed', 'Thorough'];

const Orchestrator = ({ embedded = false }) => {
  const [userMessage, setUserMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [speed, setSpeed] = useState(2); // 0 = fastest, 4 = slowest
  const cancelToken = useRef(null);
  const lastRequestTime = useRef(0);
  const chatWindowRef = useRef(null);
  const MIN_REQUEST_INTERVAL = 2000;

  const suggestedQuestions = [
        { 
          icon: '📈', 
          label: 'Stock', 
          query: 'What is driving AAPL’s recent performance?' 
        },
        { 
          icon: '🛢️', 
          label: 'Commodity', 
          query: 'What is happening with gold prices, and what macro forces are driving the move?' 
        },
        { 
          icon: '💱', 
          label: 'Forex', 
          query: 'How is EUR/USD trending, and what factors are influencing the currency pair right now?' 
        },
        { 
          icon: '₿', 
          label: 'Crypto', 
          query: 'What trends are shaping Bitcoin’s recent price action, and what does it signal for the market?' 
        },
        { 
          icon: '📊', 
          label: 'Economy', 
          query: 'How are inflation trends evolving, and what do they imply for policy and markets?' 
        },
        { 
          icon: '🔍', 
          label: 'Insight', 
          query: 'What cross-market patterns and signals are emerging right now?' 
        },
  ];

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [chatHistory, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userMessage.trim()) return;

    // Rate limit check
    const now = Date.now();
    if (now - lastRequestTime.current < MIN_REQUEST_INTERVAL) {
      const wait = Math.ceil((MIN_REQUEST_INTERVAL - (now - lastRequestTime.current)) / 1000);
      setError(`Please wait ${wait} second${wait > 1 ? 's' : ''}`);
      return;
    }
    lastRequestTime.current = now;

    setError('');
    setLoading(true);

    // Add user message to chat immediately
    const newUserMessage = { role: 'user', content: userMessage };
    setChatHistory(prev => [...prev, newUserMessage]);
    setUserMessage('');

    // Cancel previous request if exists
    if (cancelToken.current) {
      cancelToken.current.cancel('New request started');
    }
    cancelToken.current = axios.CancelToken.source();

    try {
      const response = await axiosInstance.post('/ask_fina/', {
        customer_request: userMessage,
        chat_history: chatHistory,
        speed: speed,
        context: {}
      }, {
        cancelToken: cancelToken.current.token
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // Add assistant response
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        images: response.data.new_images || []
      };
      setChatHistory(prev => [...prev, assistantMessage]);

    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request cancelled');
        return;
      }
      if (err.response?.status === 429) {
        setError('Please wait a moment before sending another request');
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred');
      }
      // Remove the user message if request failed
      setChatHistory(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setChatHistory([]);
    setError('');
    setUserMessage('');
    lastRequestTime.current = 0;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSuggestionClick = (query) => {
    setUserMessage(query);
  };

  const renderThinkingBarometer = () => (
    <div className={styles.speedSection}>
      <span className={styles.speedSectionLabel}>Thinking effort</span>
      <span className={styles.speedLabel}>Fast</span>
      <div className={styles.speedGrids}>
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            role="button"
            tabIndex={0}
            className={`${styles.speedCell} ${i <= speed ? styles.speedCellActive : ''} ${i === speed ? styles.speedCellSelected : ''}`}
            onClick={() => setSpeed(i)}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSpeed(i)}
            aria-label={`Speed: ${SPEED_NAMES[i]}`}
          />
        ))}
      </div>
      <span className={styles.speedLabel}>Slow</span>
    </div>
  );

  return (
    <div className={`${styles.container} ${embedded ? styles.embedded : ''}`}>
      {(!embedded || chatHistory.length > 0) && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h3>Fina Collective</h3>
            {!embedded && chatHistory.length === 0 && (
              <p className={styles.headerSubtitle}>Specialist agents collaborating in parallel</p>
            )}
          </div>
          {chatHistory.length > 0 && (
            <button onClick={handleClear} className={styles.clearButton}>
              Clear
            </button>
          )}
        </div>
      )}

      <div 
        className={`${styles.chatWindow} ${chatHistory.length === 0 ? styles.chatWindowEmpty : ''}`} 
        ref={chatWindowRef}
      >
        {chatHistory.length === 0 ? (
          <div className={styles.placeholder}>
            <p>What should we investigate?</p>
            {embedded && (
              <p className={styles.placeholderSub}>Specialist agents collaborating in parallel</p>
            )}
            
            <form onSubmit={handleSubmit} className={styles.inputForm}>
              <div className={styles.inputTopRow}>
                <textarea
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Collective intelligence for complex problems..."
                  rows={1}
                  className={styles.input}
                  disabled={loading}
                />
              </div>
              <div className={styles.inputBottomRow}>
                {renderThinkingBarometer()}
                <button
                  type="submit"
                  disabled={loading || !userMessage.trim()}
                  className={styles.submitButton}
                >
                  {loading ? '' : ''}
                </button>
              </div>
            </form>

            <div className={styles.categoryPills}>
              {suggestedQuestions.map((item, index) => (
                <button
                  key={index}
                  className={styles.categoryPill}
                  onClick={() => handleSuggestionClick(item.query)}
                >
                  <span className={styles.categoryPillIcon}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          chatHistory.map((message, index) => (
            <div
              key={index}
              className={`${styles.message} ${styles[message.role]}`}
            >
              <div className={styles.messageContent}>
                {message.role === 'user' ? (
                  <span>{message.content}</span>
                ) : (
                  <>
                    <SummaryDisplayChat content={message.content} />
                    {message.images?.map((imgUrl, imgIndex) => (
                      <div key={imgIndex} className={styles.imageContainer}>
                        <img src={imgUrl} alt={`Chart ${imgIndex + 1}`} className={styles.chartImage} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          ))
        )}

        {loading && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.messageRole}>Fina</div>
            <div className={styles.messageContent}>
              <div className={styles.loading}>
                <span>Agents coordinating</span>
                <span className={styles.dots}></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Input form at bottom when chat has messages */}
      {chatHistory.length > 0 && (
        <>
          <form onSubmit={handleSubmit} className={styles.inputForm}>
            <div className={styles.inputTopRow}>
              <textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Analyze macro risks affecting emerging market equities..."
                rows={1}
                className={styles.input}
                disabled={loading}
              />
            </div>
            <div className={styles.inputBottomRow}>
              {renderThinkingBarometer()}
              <button
                type="submit"
                disabled={loading || !userMessage.trim()}
                className={styles.submitButton}
              >
                {loading ? '' : ''}
              </button>
            </div>
          </form>
        </>
      )}
      <div className={styles.backLinkContainer}>
        <Link to="/">Back</Link>
      </div>
    </div>
  );
};

export default Orchestrator;
